import React, { useMemo } from "react";
import {
    ArrowUpDown,
    ChevronLeft,
    ChevronRight,
    Pencil,
    Trash2,
    Eye,
} from "lucide-react";
import {
    detectType,
    formatValue,
    isStatusKey,
    StatusBadge,
} from "../lib/utils-fields";
import { buildTableColumns } from "../lib/tableColumns";

function DkAsamaBadge({ value }) {
    if (!value) return <span className="text-slate-600">—</span>;
    const tone =
        value === "Hesaplanmadı"
            ? "bg-amber-500/15 text-amber-200 border-amber-500/30"
            : value === "Tahkim"
              ? "bg-violet-500/15 text-violet-200 border-violet-500/30"
              : value === "Tahsilat"
                ? "bg-emerald-500/15 text-emerald-200 border-emerald-500/30"
                : "bg-cyan-500/15 text-cyan-200 border-cyan-500/30";
    return (
        <span
            className={`inline-flex max-w-full truncate rounded px-1.5 py-0.5 text-[10px] border ${tone}`}
            title={String(value)}
        >
            {value}
        </span>
    );
}
function CellText({ children }) {
    if (children === null || children === undefined || children === "") {
        return <span className="text-slate-600">—</span>;
    }
    if (typeof children !== "string" && typeof children !== "number") {
        return <div className="truncate">{children}</div>;
    }
    const text = String(children);
    return (
        <div className="truncate" title={text}>
            {children}
        </div>
    );
}

/**
 * Generic, data-rich table.
 * - Auto-detects ALL fields returned by the API.
 * - Uses entity field config for friendlier labels when available.
 * - Renders a status pill for `durum`/`status` keys.
 * - Horizontal scroll for wide tables.
 * - Sticky header. Sortable columns. Pagination handled by parent (server-side).
 */
export default function DataTable({
    rows = [],
    fields = [],
    listColumns = null,
    listColumnLabels = {},
    columnWidths = {},
    columnDisplayKeys = {},
    loading = false,
    sortKey,
    sortDir, // "asc" | "desc"
    onSort,
    onEdit,
    onDelete,
    onView,
    onRowClick,
    page = 1,
    pageSize = 25,
    total = 0,
    onPageChange,
    customActions = null, // (row) => ReactNode — extra buttons before edit/delete
    rowClassName = null, // (row) => string — extra classes per row
    rowTitle = null, // (row) => string | undefined — native title tooltip on row
    cellRenderers = null, // { [columnKey]: (row, col) => ReactNode }
    actionsColumnClass = "w-[7.5rem]", // sticky actions column width
    readOnlyCrud = false,
}) {
    const columns = useMemo(
        () =>
            buildTableColumns({
                rows,
                fields,
                listColumns,
                listColumnLabels,
                columnWidths,
                displayKeys: columnDisplayKeys,
            }),
        [rows, fields, listColumns, listColumnLabels, columnWidths, columnDisplayKeys],
    );

    const totalPages = Math.max(1, Math.ceil((total || rows.length) / pageSize));

    return (
        <div
            data-testid="data-table"
            className="rounded-lg border border-white/10 bg-[#121A2F] overflow-hidden"
        >
            <div className="overflow-x-auto">
                <table className="w-full text-xs">
                    <thead className="bg-[#0B0F19]/60 sticky top-0 z-10">
                        <tr className="border-b border-white/10">
                            {columns.map((col) => {
                                const sorted = sortKey === col.key;
                                return (
                                    <th
                                        key={col.key}
                                        scope="col"
                                        className={`text-left text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-400 px-2.5 py-2 whitespace-nowrap ${col.cellClass || ""}`}
                                    >
                                        <button
                                            type="button"
                                            onClick={() => onSort?.(col.key)}
                                            className={`inline-flex items-center gap-1 transition-colors ${
                                                sorted
                                                    ? "text-[#F3E5AB]"
                                                    : "hover:text-slate-200"
                                            }`}
                                        >
                                            {col.label}
                                            <ArrowUpDown className="h-2.5 w-2.5 opacity-60 shrink-0" />
                                            {sorted && (
                                                <span className="text-[9px]">
                                                    {sortDir === "desc" ? "↓" : "↑"}
                                                </span>
                                            )}
                                        </button>
                                    </th>
                                );
                            })}
                            <th className="text-right text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-400 px-2 py-2 whitespace-nowrap sticky right-0 bg-[#0B0F19]/95 backdrop-blur-sm w-[7.5rem]">
                                İşlem
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr>
                                <td
                                    className="px-6 py-12 text-center text-slate-500"
                                    colSpan={columns.length + 1}
                                >
                                    <div className="flex items-center justify-center gap-3">
                                        <div className="h-4 w-4 rounded-full border-2 border-[#D4AF37] border-t-transparent animate-spin" />
                                        Veriler yükleniyor...
                                    </div>
                                </td>
                            </tr>
                        )}
                        {!loading && rows.length === 0 && (
                            <tr>
                                <td
                                    className="px-6 py-16 text-center text-slate-500"
                                    colSpan={columns.length + 1}
                                >
                                    <div className="flex flex-col items-center gap-2">
                                        <div className="h-12 w-12 rounded-full bg-white/5 flex items-center justify-center">
                                            <Eye className="h-5 w-5 text-slate-600" />
                                        </div>
                                        <div className="font-display text-lg text-slate-300">
                                            Kayıt bulunamadı
                                        </div>
                                        <div className="text-xs">
                                            Filtreleri değiştirin veya yeni kayıt
                                            ekleyin.
                                        </div>
                                    </div>
                                </td>
                            </tr>
                        )}
                        {!loading &&
                            rows.map((row, idx) => (
                                <tr
                                    key={row.id ?? idx}
                                    role={onRowClick ? "button" : undefined}
                                    tabIndex={onRowClick ? 0 : undefined}
                                    onClick={(e) => {
                                        if (e.target.closest("[data-stop-row-click]")) {
                                            return;
                                        }
                                        onRowClick?.(row, e);
                                    }}
                                    onKeyDown={(e) => {
                                        if (
                                            onRowClick &&
                                            (e.key === "Enter" || e.key === " ")
                                        ) {
                                            if (e.target.closest("[data-stop-row-click]")) {
                                                return;
                                            }
                                            e.preventDefault();
                                            onRowClick(row, e);
                                        }
                                    }}
                                    className={`table-row-hover group border-b border-white/5 last:border-0 transition-colors ${
                                        onRowClick
                                            ? "cursor-pointer hover:bg-[#D4AF37]/[0.06]"
                                            : ""
                                    } ${rowClassName?.(row) || ""}`}
                                    title={rowTitle?.(row) || undefined}
                                >
                                    {columns.map((col) => {
                                        const custom = cellRenderers?.[col.key];
                                        if (custom) {
                                            return (
                                                <td
                                                    key={col.key}
                                                    className={`px-2.5 py-1.5 align-middle text-slate-200 ${col.cellClass || "max-w-[9rem]"}`}
                                                >
                                                    {custom(row, col)}
                                                </td>
                                            );
                                        }
                                        const rawValue = row?.[col.key];
                                        const displayKey = col.displayKey;
                                        const displayValue = displayKey
                                            ? row?.[displayKey]
                                            : null;
                                        const v =
                                            displayValue !== undefined &&
                                            displayValue !== null &&
                                            displayValue !== ""
                                                ? displayValue
                                                : rawValue;
                                        const t =
                                            col.type ||
                                            detectType(col.key, v);
                                        return (
                                            <td
                                                key={col.key}
                                                className={`px-2.5 py-1.5 align-middle text-slate-200 whitespace-nowrap ${col.cellClass || "max-w-[9rem]"}`}
                                            >
                                                {col.key === "dk_asama_label" ? (
                                                    <DkAsamaBadge value={v} />
                                                ) : isStatusKey(col.key) ||
                                                (col.displayKey &&
                                                    isStatusKey(
                                                        col.displayKey.replace(
                                                            /_display$/,
                                                            "",
                                                        ),
                                                    )) ? (
                                                    <StatusBadge value={v} />
                                                ) : col.key === "kusur_orani" &&
                                                  v !== null &&
                                                  v !== undefined &&
                                                  v !== "" ? (
                                                    <span className="font-mono-jb text-[11px] tabular-nums">
                                                        {v}%
                                                    </span>
                                                ) : (
                                                    <CellText>
                                                        {formatValue(
                                                            v,
                                                            t,
                                                            col.key,
                                                        )}
                                                    </CellText>
                                                )}
                                            </td>
                                        );
                                    })}
                                    <td className={`px-2 py-1.5 text-right whitespace-nowrap sticky right-0 bg-[#121A2F] group-hover:bg-[#1E293B] ${actionsColumnClass}`}>
                                        <div className="inline-flex items-center gap-0.5">
                                            {customActions?.(row)}
                                            {onView && (
                                                <button
                                                    data-testid={`row-view-${row.id ?? idx}`}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        onView(row);
                                                    }}
                                                    className="h-7 w-7 rounded-md hover:bg-white/5 flex items-center justify-center text-slate-400 hover:text-[#F3E5AB] transition"
                                                    title="Detay"
                                                >
                                                    <Eye className="h-3.5 w-3.5" />
                                                </button>
                                            )}
                                            {!readOnlyCrud && onEdit && (
                                                <button
                                                    data-testid={`row-edit-${row.id ?? idx}`}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        onEdit(row);
                                                    }}
                                                    className="h-7 w-7 rounded-md hover:bg-white/5 flex items-center justify-center text-slate-400 hover:text-[#F3E5AB] transition"
                                                    title="Düzenle"
                                                >
                                                    <Pencil className="h-3.5 w-3.5" />
                                                </button>
                                            )}
                                            {!readOnlyCrud && onDelete && (
                                                <button
                                                    data-testid={`row-delete-${row.id ?? idx}`}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        onDelete(row);
                                                    }}
                                                    className="h-7 w-7 rounded-md hover:bg-rose-500/10 flex items-center justify-center text-slate-400 hover:text-rose-400 transition"
                                                    title="Sil"
                                                >
                                                    <Trash2 className="h-3.5 w-3.5" />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                    </tbody>
                </table>
            </div>

            {/* Footer / pagination */}
            <div className="flex items-center justify-between gap-3 px-3 py-2 border-t border-white/10 bg-[#0B0F19]/40">
                <div className="text-[11px] text-slate-500">
                    <span className="font-mono-jb text-slate-300">
                        {total > 0
                            ? `${(page - 1) * pageSize + 1}–${Math.min(
                                  page * pageSize,
                                  total,
                              )}`
                            : "0"}
                    </span>{" "}
                    /{" "}
                    <span className="font-mono-jb text-slate-300">
                        {total}
                    </span>{" "}
                    kayıt
                </div>
                <div className="flex items-center gap-2">
                    <button
                        data-testid="pagination-prev"
                        disabled={page <= 1}
                        onClick={() => onPageChange?.(Math.max(1, page - 1))}
                        className="h-8 px-2 rounded-md border border-white/10 text-slate-300 hover:bg-white/5 disabled:opacity-30 disabled:cursor-not-allowed inline-flex items-center gap-1 text-xs"
                    >
                        <ChevronLeft className="h-3.5 w-3.5" /> Önceki
                    </button>
                    <span className="text-xs text-slate-400 font-mono-jb px-2">
                        {page} / {totalPages}
                    </span>
                    <button
                        data-testid="pagination-next"
                        disabled={page >= totalPages}
                        onClick={() =>
                            onPageChange?.(Math.min(totalPages, page + 1))
                        }
                        className="h-8 px-2 rounded-md border border-white/10 text-slate-300 hover:bg-white/5 disabled:opacity-30 disabled:cursor-not-allowed inline-flex items-center gap-1 text-xs"
                    >
                        Sonraki <ChevronRight className="h-3.5 w-3.5" />
                    </button>
                </div>
            </div>
        </div>
    );
}
